/**
 * Unified Game Design System Configuration
 * Centralized configuration for all games - colors, fonts, sizes, etc.
 */

export const GAME_CONFIG = {
  // Color Palette
  colors: {
    primary: '#667eea',
    success: '#48bb78',
    warning: '#f6ad55',
    danger: '#fc8181',
    info: '#4299e1',
    secondary: '#a0aec0',
    dark: '#1a1a1a',
    darker: '#0f0f0f',
    light: '#f1f5f9',
    white: '#ffffff',
    canvas: '#000000',
    accent: '#00d4ff', // Cyan
    accent2: '#00ff88', // Lime
  },

  // Bootstrap Color Classes
  bootstrapColors: {
    primary: 'primary',
    success: 'success',
    warning: 'warning',
    danger: 'danger',
    info: 'info',
    secondary: 'secondary',
  },

  // Typography
  fonts: {
    family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    weights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  // Spacing
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },

  // Border Radius
  radius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },

  // Transitions
  transitions: {
    fast: '150ms ease-in-out',
    base: '250ms ease-in-out',
    slow: '350ms ease-in-out',
  },

  // Game Settings
  game: {
    defaultFPS: 60,
    canvasBackground: '#000000',
    textColor: '#f1f5f9',
    scoreHeight: '60px',
    controlsHeight: '60px',
    minCanvasWidth: 320,
    minCanvasHeight: 240,
  },

  // Responsive Breakpoints (Tailwind)
  breakpoints: {
    xs: '0px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },

  // Difficulty Levels
  difficulty: {
    EASY: 'easy',
    NORMAL: 'normal',
    HARD: 'hard',
    EXPERT: 'expert',
  },

  // Game States
  states: {
    MENU: 'menu',
    PLAYING: 'playing',
    PAUSED: 'paused',
    GAME_OVER: 'gameOver',
    WIN: 'win',
    LOADING: 'loading',
  },

  // Button Sizes
  buttonSizes: {
    sm: 'btn-sm',
    md: 'btn',
    lg: 'btn-lg',
  },

  // Z-Index Layers
  zIndex: {
    base: 1,
    dropdown: 100,
    sticky: 1000,
    fixed: 1050,
    modalBackdrop: 1040,
    modal: 1060,
    popover: 1070,
    tooltip: 1080,
    notification: 2000,
  },
};

// Utility function to get CSS color from config
export const getColor = (colorName) => {
  return GAME_CONFIG.colors[colorName] || GAME_CONFIG.colors.primary;
};

// Utility function to convert RGB colors for canvas
export const getRGBColor = (colorName) => {
  const hex = getColor(colorName);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`;
  }
  return hex;
};

export default GAME_CONFIG;
